import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookUser, RefreshCw, Search, Mail, Phone, MapPin, Building2, UserPlus, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import NewClientModal from '@/components/clients/NewClientModal';

type ContactsStatus = {
  connected: boolean;
  contactsScopeGranted: boolean;
  contactsSyncInProgress: boolean;
  contactsSyncProcessed: number;
  lastContactsSyncAt: string | null;
  lastContactsSyncError: string | null;
  contactsWriteEnabled: boolean;
  totalCached: number;
};

type ContactRow = {
  resourceName: string;
  displayName: string;
  primaryEmail?: string | null;
  primaryPhone?: string | null;
  source: string;
  organizations?: { name?: string; title?: string }[];
  linkedClient?: { id: string; firstName: string; lastName: string } | null;
};

type ContactDetail = ContactRow & {
  emails?: { value: string }[];
  phones?: { value: string }[];
  addresses?: { formatted?: string; city?: string; region?: string }[];
  biography?: string;
};

type SuggestedClient = {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  googleResourceName?: string | null;
};

function withToken(init: RequestInit = {}): RequestInit {
  const token = localStorage.getItem('accessToken');
  return {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
      Authorization: `Bearer ${token}`,
    },
  };
}

export default function ContactsPage() {
  const [status, setStatus] = useState<ContactsStatus | null>(null);
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ContactDetail | null>(null);
  const [suggestedClient, setSuggestedClient] = useState<SuggestedClient | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [clientPrefill, setClientPrefill] = useState<Record<string, string> | undefined>();

  const loadStatus = useCallback(async () => {
    const res = await fetch('/api/gmail/contacts/status', withToken());
    const data = await res.json();
    if (data.success) setStatus(data.data);
  }, []);

  const loadList = useCallback(async (q?: string) => {
    setLoading(true);
    try {
      if (q && q.trim().length >= 2) {
        const res = await fetch(
          `/api/gmail/contacts/search?q=${encodeURIComponent(q.trim())}`,
          withToken()
        );
        const data = await res.json();
        if (data.success) setContacts(data.data.contacts ?? []);
      } else {
        const res = await fetch('/api/gmail/contacts?page=1&limit=10000', withToken());
        const data = await res.json();
        if (data.success) setContacts(data.data.contacts ?? []);
      }
    } catch {
      toast.error('Errore caricamento rubrica');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
    void loadList();
  }, [loadStatus, loadList]);

  useEffect(() => {
    if (!status?.contactsSyncInProgress) return;
    const id = setInterval(() => {
      void loadStatus();
      void loadList(search);
    }, 3000);
    return () => clearInterval(id);
  }, [status?.contactsSyncInProgress, loadStatus, loadList, search]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch('/api/gmail/contacts/sync', withToken({ method: 'POST' }));
      const data = await res.json();
      if (data.success) {
        toast.success(data.message || 'Sincronizzazione avviata');
        await loadStatus();
      } else {
        toast.error(data.message || 'Sync fallita');
      }
    } catch {
      toast.error('Sync fallita');
    } finally {
      setSyncing(false);
    }
  };

  const openDetail = async (resourceName: string) => {
    try {
      const res = await fetch(
        `/api/gmail/contacts/detail?resourceName=${encodeURIComponent(resourceName)}`,
        withToken()
      );
      const data = await res.json();
      if (data.success) {
        setSelected(data.data.contact);
        setSuggestedClient(data.data.suggestedClient ?? null);
      }
    } catch {
      toast.error('Errore caricamento contatto');
    }
  };

  const handleCreateClient = async () => {
    if (!selected) return;
    const res = await fetch(
      `/api/gmail/contacts/import-prefill?resourceName=${encodeURIComponent(selected.resourceName)}`,
      withToken()
    );
    const data = await res.json();
    if (data.success && data.data?.prefill) {
      setClientPrefill(data.data.prefill);
      setShowClientModal(true);
    } else {
      toast.error('Impossibile preparare anagrafica cliente');
    }
  };

  const handleLinkClient = async () => {
    if (!selected || !suggestedClient) return;
    const res = await fetch('/api/gmail/contacts/link-client', withToken({
      method: 'POST',
      body: JSON.stringify({
        clientId: suggestedClient.id,
        resourceName: selected.resourceName,
      }),
    }));
    const data = await res.json();
    if (data.success) {
      toast.success('Cliente collegato');
      void openDetail(selected.resourceName);
      void loadList(search);
    } else {
      toast.error(data.message || 'Collegamento fallito');
    }
  };

  const handlePublish = async () => {
    if (!selected) return;
    try {
      const res = await fetch('/api/gmail/contacts/publish', withToken({
        method: 'POST',
        body: JSON.stringify({
          resourceName: selected.resourceName,
          contact: selected,
        }),
      }));
      const data = await res.json();
      if (data.success) {
        toast.success('Contatto pubblicato su Google');
        void openDetail(data.data.resourceName);
      } else {
        toast.error(data.message || 'Pubblicazione fallita');
      }
    } catch {
      toast.error('Pubblicazione fallita');
    }
  };

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <BookUser className="h-7 w-7" />
            Rubrica Google
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Contatti della casella collegata.{' '}
            <Link to="/email" className="underline">
              Vai a Email
            </Link>
          </p>
        </div>
        <Button onClick={handleSync} disabled={syncing || status?.contactsSyncInProgress}>
          <RefreshCw
            className={`h-4 w-4 mr-2 ${status?.contactsSyncInProgress ? 'animate-spin' : ''}`}
          />
          Sincronizza rubrica
        </Button>
      </div>

      {!status?.connected && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="pt-4 text-sm text-amber-900">
            Gmail non collegato. Un amministratore deve collegare la casella da Impostazioni → Email.
          </CardContent>
        </Card>
      )}

      {status?.connected && !status.contactsScopeGranted && (
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="pt-4 text-sm text-red-900">
            Ambito rubrica mancante: disconnetti e ricollega Gmail accettando anche i contatti (vedi guida
            setup Google).
          </CardContent>
        </Card>
      )}

      {status?.contactsSyncInProgress && (
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm font-medium">Sincronizzazione in corso…</p>
            <p className="text-xs text-muted-foreground mt-1">
              Elaborati: {status.contactsSyncProcessed} contatti
            </p>
            <div className="mt-2 h-2 w-full rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary animate-pulse w-2/3" />
            </div>
          </CardContent>
        </Card>
      )}

      {status?.lastContactsSyncError && (
        <p className="text-xs text-red-600">Ultimo errore sync: {status.lastContactsSyncError}</p>
      )}

      <div className="flex gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Cerca nome o email (min. 2 caratteri)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void loadList(search)}
          />
        </div>
        <Button variant="outline" onClick={() => void loadList(search)}>
          Cerca
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        {status?.totalCached ?? 0} contatti in cache
        {status?.lastContactsSyncAt &&
          ` · Ultimo sync: ${new Date(status.lastContactsSyncAt).toLocaleString('it-IT')}`}
      </p>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="min-h-[320px]">
          <CardHeader>
            <CardTitle className="text-base">Elenco</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <p className="p-4 text-sm text-muted-foreground">Caricamento…</p>
            ) : contacts.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">
                Nessun contatto. Avvia la sincronizzazione.
              </p>
            ) : (
              <ul className="max-h-[480px] overflow-y-auto divide-y">
                {contacts.map((c) => (
                  <li key={c.resourceName}>
                    <button
                      type="button"
                      className={`w-full text-left px-4 py-3 hover:bg-muted/50 ${
                        selected?.resourceName === c.resourceName ? 'bg-muted' : ''
                      }`}
                      onClick={() => void openDetail(c.resourceName)}
                    >
                      <div className="font-medium">{c.displayName}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {c.primaryEmail || c.primaryPhone || '—'}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="min-h-[320px]">
          <CardHeader>
            <CardTitle className="text-base">Dettaglio</CardTitle>
          </CardHeader>
          <CardContent>
            {!selected ? (
              <p className="text-sm text-muted-foreground">Seleziona un contatto dall&apos;elenco.</p>
            ) : (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold">{selected.displayName}</h2>
                {selected.emails?.map((e) => (
                  <div key={e.value} className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    {e.value}
                  </div>
                ))}
                {selected.phones?.map((p) => (
                  <div key={p.value} className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    {p.value}
                  </div>
                ))}
                {selected.addresses?.[0]?.formatted && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    {selected.addresses[0].formatted}
                  </div>
                )}
                {selected.organizations?.[0]?.name && (
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    {selected.organizations[0].name}
                    {selected.organizations[0].title && ` — ${selected.organizations[0].title}`}
                  </div>
                )}
                {selected.biography && (
                  <p className="text-sm text-muted-foreground border-t pt-3">{selected.biography}</p>
                )}

                {suggestedClient && !suggestedClient.googleResourceName && (
                  <div className="rounded-md border border-blue-200 bg-blue-50/50 p-3 text-sm">
                    Esiste un cliente con la stessa email: {suggestedClient.firstName}{' '}
                    {suggestedClient.lastName}. Collegare?
                    <Button size="sm" className="mt-2" variant="outline" onClick={handleLinkClient}>
                      Collega cliente
                    </Button>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  <Button size="sm" onClick={handleCreateClient}>
                    <UserPlus className="h-4 w-4 mr-1" />
                    Crea cliente
                  </Button>
                  {status?.contactsWriteEnabled && (
                    <Button size="sm" variant="outline" onClick={handlePublish}>
                      <Upload className="h-4 w-4 mr-1" />
                      Pubblica su Google
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <NewClientModal
        isOpen={showClientModal}
        onClose={() => {
          setShowClientModal(false);
          setClientPrefill(undefined);
        }}
        prefillFromGoogle={clientPrefill}
      />
    </div>
  );
}
